import { useCallback, useEffect, useRef, useState } from 'react';
import { 
  ChefHat,
  Clock, 
  CheckCircle2, 
  AlertCircle,
  Flame,
  Package,
  Truck,
  User,
  RefreshCw
} from 'lucide-react';
import { PageErrorBoundary } from '../ErrorBoundary';
import { api } from '../../lib/api';

interface Order {
  id: string;
  orderNumber: string;
  customerName: string;
  customerPhone: string;
  status: string;
  estimatedTime: number;
  createdAt: string;
  confirmedAt?: string;
  items: Array<{
    id: string;
    name: string;
    quantity: number;
    price: number;
    variant_name?: string;
    addons?: string;
    special_instructions?: string;
  }>;
}

interface KitchenOrdersProps {
  restaurantId: string;
}

const mapKitchenOrder = (order: any): Order => ({
  id: String(order?.id || ''),
  orderNumber: String(order?.orderNumber || order?.id || ''),
  customerName: String(order?.customer?.name || order?.customerName || 'Customer'),
  customerPhone: String(order?.customer?.phone || order?.customerPhone || 'N/A'),
  status: String(order?.status || 'PENDING').toUpperCase(),
  estimatedTime: Number(order?.estimatedTime || 30),
  createdAt: String(order?.createdAt || order?.placedAt || new Date().toISOString()),
  confirmedAt: order?.acceptedAt ? String(order.acceptedAt) : undefined,
  items: Array.isArray(order?.items)
    ? order.items.map((item: any) => ({
        id: String(item?.id || ''),
        name: String(item?.menuItem?.name || item?.name || 'Item'),
        quantity: Number(item?.quantity || 0),
        price: Number(item?.price || 0),
        special_instructions: item?.notes || undefined,
      }))
    : [],
});

export function KitchenOrders({ restaurantId }: KitchenOrdersProps) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const inFlightRef = useRef(false);
  const hasLoadedOnceRef = useRef(false);

  const loadKitchenOrders = useCallback(async (source: 'initial' | 'manual' | 'interval' = 'manual') => {
    if (!restaurantId) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      setError(null);
      if (!hasLoadedOnceRef.current && source === 'initial') {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      const response = await api.orders.getAll({ restaurantId, limit: 100 } as any);
      const rawOrders = Array.isArray((response as any)?.data)
        ? (response as any).data
        : Array.isArray((response as any)?.data?.orders)
        ? (response as any).data.orders
        : [];

      setOrders(
        rawOrders
          .map(mapKitchenOrder)
          .filter((order: Order) => ['CONFIRMED', 'PREPARING'].includes(order.status))
      );
    } catch (err: any) {
      console.error('Error loading kitchen orders:', err);
      setError(err.message || 'Failed to load kitchen orders');
    } finally {
      setLoading(false);
      setRefreshing(false);
      hasLoadedOnceRef.current = true;
      inFlightRef.current = false;
    }
  }, [restaurantId]);

  useEffect(() => {
    void loadKitchenOrders('initial');

    // Auto-refresh every 15 seconds
    const interval = setInterval(() => {
      void loadKitchenOrders('interval');
    }, 15000);

    return () => clearInterval(interval);
  }, [loadKitchenOrders]);

  const updateOrderStatus = async (orderId: string, newStatus: string) => {
    try {
      setError(null);
      await api.orders.updateStatus(orderId, newStatus);

      const statusMessages: Record<string, string> = {
        PREPARING: 'Order is now being prepared',
        READY: 'Order is ready for handoff to admin/manager',
      };

      setSuccess(statusMessages[newStatus] || 'Order status updated');
      await loadKitchenOrders('manual');
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.message || 'Failed to update order status');
    }
  };

  const getStatusColor = (status: string) => {
    const colors: any = {
      CONFIRMED: 'bg-blue-100 text-blue-700 border-blue-200',
      PREPARING: 'bg-orange-100 text-orange-700 border-orange-200',
      READY: 'bg-green-100 text-green-700 border-green-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getStatusIcon = (status: string) => {
    const icons: any = {
      CONFIRMED: <CheckCircle2 className="w-4 h-4" />,
      PREPARING: <Flame className="w-4 h-4" />,
      READY: <Package className="w-4 h-4" />
    };
    return icons[status] || <Clock className="w-4 h-4" />;
  };

  const getNextAction = (status: string) => {
    const actions: any = {
      CONFIRMED: { status: 'PREPARING', label: 'Start Preparing', icon: <ChefHat className="w-4 h-4" />, color: 'from-orange-500 to-red-600' },
      PREPARING: { status: 'READY', label: 'Mark as Ready', icon: <CheckCircle2 className="w-4 h-4" />, color: 'from-green-500 to-emerald-600' }
    };
    return actions[status];
  };

  const getTimeSince = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 minute ago';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) return '1 hour ago';
    return `${diffHours} hours ago`;
  };

  const filteredOrders = selectedStatus === 'all' 
    ? orders 
    : orders.filter(o => o.status === selectedStatus);

  const statusCounts = {
    all: orders.length,
    CONFIRMED: orders.filter(o => o.status === 'CONFIRMED').length,
    PREPARING: orders.filter(o => o.status === 'PREPARING').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading kitchen orders...</p>
        </div>
      </div>
    );
  }

  return (
    <PageErrorBoundary>
      <div className="space-y-4">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <ChefHat className="w-8 h-8" />
                <h2 className="text-2xl font-bold">Kitchen Orders</h2>
              </div>
              <p className="text-orange-100">Manage and track order preparation</p>
            </div>
            <button
              type="button"
              onClick={() => void loadKitchenOrders('manual')}
              disabled={refreshing}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-xl p-3 transition-colors disabled:cursor-not-allowed disabled:opacity-70"
              title="Refresh orders"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Status Filter Tabs */}
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <div className="flex gap-2 flex-wrap">
            {[
              { key: 'all', label: 'All Orders', count: statusCounts.all },
              { key: 'CONFIRMED', label: 'New', count: statusCounts.CONFIRMED },
              { key: 'PREPARING', label: 'Preparing', count: statusCounts.PREPARING }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setSelectedStatus(tab.key)}
                className={`px-4 py-2 rounded-xl font-medium transition-colors ${
                  selectedStatus === tab.key
                    ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tab.label}
                <span className="ml-2 px-2 py-0.5 rounded-full bg-white bg-opacity-20 text-sm">
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Messages */}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-xl flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5" />
            {success}
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {/* Orders Grid */}
        {filteredOrders.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <ChefHat className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Orders</h3>
            <p className="text-gray-600">
              {selectedStatus === 'all' ? 'No active orders in kitchen' : `No orders with status: ${selectedStatus}`}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredOrders.map((order) => {
              const nextAction = getNextAction(order.status);
              
              return (
                <div key={order.id} className="bg-white rounded-2xl shadow-sm p-6 border-2 hover:border-orange-200 transition-colors">
                  {/* Order Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="text-lg font-bold text-gray-900 mb-1">
                        #{order.orderNumber}
                      </div>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-600">{order.customerName}</span>
                      </div>
                    </div>
                    <div className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-sm font-medium border ${getStatusColor(order.status)}`}>
                      {getStatusIcon(order.status)}
                      <span>{order.status.split('_').join(' ')}</span>
                    </div>
                  </div>

                  {/* Time Info */}
                  <div className="flex items-center gap-4 mb-4 text-sm">
                    <div className="flex items-center gap-1 text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span>{getTimeSince(order.confirmedAt || order.createdAt)}</span>
                    </div>
                    {order.estimatedTime > 0 && (
                      <div className="flex items-center gap-1 text-orange-600">
                        <AlertCircle className="w-4 h-4" />
                        <span>{order.estimatedTime} mins</span>
                      </div>
                    )}
                  </div>

                  {/* Order Items */}
                  <div className="mb-4 bg-gray-50 rounded-xl p-4">
                    <div className="text-sm font-medium text-gray-900 mb-3">Items:</div>
                    <div className="space-y-3">
                      {order.items.map((item) => (
                        <div key={item.id} className="space-y-1">
                          <div className="flex justify-between items-start">
                            <span className="text-sm font-medium text-gray-900">
                              {item.quantity}x {item.name}
                            </span>
                          </div>
                          {item.variant_name && (
                            <div className="text-xs text-gray-600 pl-4">
                              Variant: {item.variant_name}
                            </div>
                          )}
                          {item.addons && (
                            <div className="text-xs text-gray-600 pl-4">
                              Add-ons: {JSON.parse(item.addons).map((a: any) => a.name).join(', ')}
                            </div>
                          )}
                          {item.special_instructions && (
                            <div className="text-xs text-orange-600 pl-4 italic bg-orange-50 border border-orange-200 rounded p-2 mt-1">
                              📝 {item.special_instructions}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action Button */}
                  {nextAction && (
                    <button
                      onClick={() => updateOrderStatus(order.id, nextAction.status)}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r ${nextAction.color} text-white rounded-xl hover:shadow-lg transition-all font-medium`}
                    >
                      {nextAction.icon}
                      {nextAction.label}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PageErrorBoundary>
  );
}
